/**
 * Active-tenant resolution (E25-8).
 *
 * The dashboard is a SINGLE build that serves many tenants; which tenant a given
 * page load is scoped to is resolved at runtime and stamped onto every API call
 * as the `X-Tenant` header (see `api/client.ts`). Resolution order:
 *
 *   1. Domain   — `<tenant>.keystone.brightworkdigital.co` → the leftmost label.
 *   2. Login    — a tenant persisted at login (`localStorage["keystone.tenant"]`).
 *   3. Dev      — `VITE_TENANT` env, else "mac", for localhost / preview.
 *
 * The CLIENT value is a HINT only — the authoritative tenant binding is enforced
 * server-side by E25-7 (#181 TenantUser). Kept a pure function reading
 * `window` / `localStorage` / `import.meta.env` so it is unit-testable.
 */

/** localStorage key the auth flow may set to pin the tenant after login. */
export const TENANT_STORAGE_KEY = "keystone.tenant";

/** Dev/single-tenant fallback so the current local workflow is unchanged. */
export const DEFAULT_TENANT = "mac";

/**
 * Hostnames whose leftmost label is NOT a tenant slug. `keystone` itself is the
 * apex of the dashboard host; `www` and the bare domain are not tenants either.
 */
const RESERVED_SUBDOMAINS = new Set(["www", "keystone", "app", "dashboard"]);

/** A tenant slug looks like a lowercase DNS label (letters, digits, hyphen). */
function isValidSlug(label: string): boolean {
  return /^[a-z0-9-]+$/.test(label) && !RESERVED_SUBDOMAINS.has(label);
}

/**
 * Parse `<tenant>.keystone.…` from a hostname. Returns the leftmost label when
 * the host is a multi-label subdomain of `keystone.*`, else `null`. localhost,
 * IPs and bare/2-label hosts (no subdomain) yield `null` so we fall through to
 * the login/dev resolution.
 */
function tenantFromHostname(hostname: string): string | null {
  const host = hostname.toLowerCase();
  // No subdomain (localhost, example.com, an IP) → not a tenant host.
  const labels = host.split(".");
  if (labels.length < 3) return null;
  // Require the `keystone` label to appear after the leftmost one so we only
  // claim subdomains of the keystone host, not arbitrary 3-label hosts.
  if (!labels.slice(1).includes("keystone")) return null;
  const candidate = labels[0];
  return candidate && isValidSlug(candidate) ? candidate : null;
}

/** Read the login-pinned tenant hint from localStorage (best-effort). */
function tenantFromStorage(): string | null {
  try {
    const value = globalThis.localStorage?.getItem(TENANT_STORAGE_KEY);
    return value && isValidSlug(value) ? value : null;
  } catch {
    return null;
  }
}

/** The dev/preview fallback from `VITE_TENANT`, else the default ("mac"). */
function devFallback(): string {
  const env = (import.meta.env?.VITE_TENANT as string | undefined)?.trim();
  return env && isValidSlug(env) ? env : DEFAULT_TENANT;
}

/**
 * Resolve the active tenant slug (domain → login → dev fallback). Pure: reads
 * only `window.location`, `localStorage` and `import.meta.env`.
 */
export function resolveTenant(): string {
  if (typeof window !== "undefined" && window.location?.hostname) {
    const fromDomain = tenantFromHostname(window.location.hostname);
    if (fromDomain) return fromDomain;
  }
  const fromStorage = tenantFromStorage();
  if (fromStorage) return fromStorage;
  return devFallback();
}
